// app/(main)/compte/profil/page.tsx
"use client";

import { useState, useEffect } from "react";
import { User, Mail, Phone, MapPin, Camera } from "lucide-react";
import { PageContainer } from "../../../components/ui/PageContainer";
import { useAuth } from "@cabukaka/hooks";
import { alertError, alertSuccess } from "../../../helpers/alertError";
import { Client } from "@cabukaka/types";

export default function ProfilePage() {
  const { user, updateUser, refreshUser } = useAuth();
  const profile = user?.profile as Client;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    address: ""
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || "",
        email: user?.email || "",
        phone: profile.phone || "",
        city: profile.location?.city || "",
        address: profile.location?.address || ""
      });
    }
  }, [profile, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      alertError("Veuillez vous connecter");
      return;
    }

    setIsLoading(true);
    try {
      await updateUser(user, {
        name: formData.name,
        phone: formData.phone,
        location: {
          city: formData.city,
          address: formData.address
        }
      });
      await refreshUser();
      alertSuccess("Profil mis à jour avec succès");
    } catch (error: any) {
      alertError(error.message || "Erreur lors de la mise à jour");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer withContainer>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo de profil */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center">
              <User className="w-12 h-12 text-primary" />
            </div>
            <button className="absolute bottom-0 right-0 p-1.5 bg-primary text-white rounded-full">
              <Camera className="w-3 h-3" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">Modifier la photo</p>
        </div>

        {/* Formulaire */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="email"
                value={formData.email}
                disabled
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">L'email ne peut pas être modifié</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary"
              />
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary resize-none"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 bg-primary text-white py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isLoading ? "Enregistrement..." : "Enregistrer les modifications"}
          </button>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="px-6 py-2 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
        </div>

        <div className="border-t border-gray-200 pt-6 mt-4">
          <h3 className="font-semibold text-red-600 mb-2">Zone dangereuse</h3>
          <button 
            type="button"
            onClick={() => {
              if (confirm("Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.")) {
                // Logique de suppression
              }
            }}
            className="text-red-600 text-sm hover:underline"
          >
            Supprimer mon compte
          </button>
        </div>
      </form>
    </PageContainer>
  );
}